import React from 'react';
import {
    Campaign as CampaignIcon,
    Dashboard as DashboardIcon,
    VideoLibrary as VideoLibraryIcon,
    Sensors as SensorsIcon,
    SmartDisplay as ClipsIcon,
} from '@mui/icons-material';
import { ROUTES_NAMES } from '../../components/routes';
import i18n from '../../i18n';

/** Explorer section ids. 0/2/3/4 double as permission indexes for hasExplorerTabAccess. */
export const EXPLORER_PROGRAMS_TAB = 0;
export const EXPLORER_BRANDS_TAB = 2;
export const EXPLORER_LIVESTREAMS_TAB = 3;
/** Legacy "Comparar" permission; grants Programas in compare mode. */
export const EXPLORER_LEGACY_COMPARE_TAB = 4;
/** Not a permission index; shown when the user can open any other section. */
export const EXPLORER_GENERAL_TAB = 5;
/** Granted by the company `clips` product, not by other Explorer tabs. */
export const EXPLORER_SOCIAL_TAB = 6;

type HasExplorerTabAccess = (tabIndex: number) => boolean;

export interface ExplorerTabDef {
    index: number;
    labelKey: 'tabs.panel' | 'tabs.sponsors' | 'tabs.channels' | 'tabs.livestreams' | 'tabs.clips';
    /** Spanish label. Prefer `t(labelKey)` from the explorer namespace. */
    label: string;
    path: string;
    icon: React.ElementType;
    canAccess: (hasAccess: HasExplorerTabAccess) => boolean;
}

export const EXPLORER_TABS: ExplorerTabDef[] = [
    {
        index: EXPLORER_GENERAL_TAB,
        labelKey: 'tabs.panel',
        label: 'Mi panel',
        path: ROUTES_NAMES.EXPLORER_PANEL,
        icon: DashboardIcon,
        canAccess: (has) =>
            has(EXPLORER_BRANDS_TAB) || has(EXPLORER_PROGRAMS_TAB) || has(EXPLORER_LIVESTREAMS_TAB),
    },
    {
        index: EXPLORER_BRANDS_TAB,
        labelKey: 'tabs.sponsors',
        label: 'Auspiciadores',
        path: ROUTES_NAMES.EXPLORER_BRANDS_LIST,
        icon: CampaignIcon,
        canAccess: (has) => has(EXPLORER_BRANDS_TAB),
    },
    {
        index: EXPLORER_PROGRAMS_TAB,
        labelKey: 'tabs.channels',
        label: 'Canales',
        path: ROUTES_NAMES.EXPLORER_PROGRAMS_LIST,
        icon: VideoLibraryIcon,
        canAccess: (has) => has(EXPLORER_PROGRAMS_TAB) || has(EXPLORER_LEGACY_COMPARE_TAB),
    },
    {
        index: EXPLORER_SOCIAL_TAB,
        labelKey: 'tabs.clips',
        label: 'Clips',
        path: ROUTES_NAMES.EXPLORER_SOCIAL_LIST,
        icon: ClipsIcon,
        canAccess: (has) => has(EXPLORER_SOCIAL_TAB),
    },
    {
        index: EXPLORER_LIVESTREAMS_TAB,
        labelKey: 'tabs.livestreams',
        label: 'Livestreams',
        path: ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST,
        icon: SensorsIcon,
        canAccess: (has) => has(EXPLORER_LIVESTREAMS_TAB),
    },
];

export const SPONSORSHIPS_REPORT_TYPE = 'explorer.sponsorships';
export const SPONSORSHIPS_REPORT_LABEL = 'Auspicios por programa';
export const SPONSORSHIPS_REPORT_DESCRIPTION = 'Cada mención con link al video.';

export const LIVESTREAMS_CHANNELS_REPORT_TYPE = 'explorer.livestreams.channels';
export const LIVESTREAMS_PROGRAMS_REPORT_TYPE = 'explorer.livestreams.programs';
/** Livestreams export cards in dialog order. */
export const LIVESTREAMS_EXPORT_REPORTS = [LIVESTREAMS_CHANNELS_REPORT_TYPE, LIVESTREAMS_PROGRAMS_REPORT_TYPE];
/** Livestream channel detail: one row per stream of that channel. */
export const LIVESTREAM_CHANNEL_REPORT_TYPE = 'explorer.livestreams.channel';
/** Livestream program detail: one row per stream of that program. */
export const LIVESTREAM_PROGRAM_REPORT_TYPE = 'explorer.livestreams.program';
/** `programIds` filter value for streams with no program. */
export const LIVESTREAM_UNASSIGNED_PROGRAM = 'none';

export const AUDIENCE_PROGRAMS_REPORT_TYPE = 'explorer.livestreams.audience.programs';
export const AUDIENCE_STREAMS_REPORT_TYPE = 'explorer.livestreams.audience.streams';
/** Mapa de audiencia export cards in dialog order. */
export const AUDIENCE_EXPORT_REPORTS = [AUDIENCE_PROGRAMS_REPORT_TYPE, AUDIENCE_STREAMS_REPORT_TYPE];
/** Livestream competition: one row per stream in the clock window. */
export const COMPETITION_REPORT_TYPE = 'explorer.livestreams.competition';
/** Auspiciadores clips: one row per brand with clip metrics. */
export const SOCIAL_BRANDS_REPORT_TYPE = 'explorer.social.brands';
/** Clips ranking: one row per channel. */
export const SOCIAL_CHANNELS_REPORT_TYPE = 'explorer.social.channels';
/** One channel's clips and posts for the selected period. */
export const SOCIAL_CHANNEL_REPORT_TYPE = 'explorer.social.channel';
/** Canales hub: On Demand + Livestreams + Clips in one XLSX. */
export const CANALES_BUNDLE_REPORT_TYPE = 'explorer.canales';
export const CANALES_SECTION_ONDEMAND = 'ondemand';
export const CANALES_SECTION_LIVESTREAMS = 'livestreams';
export const CANALES_SECTION_CLIPS = 'clips';

const EXPORT_REPORT_CARD_KEYS: Record<string, { labelKey: string; descriptionKey: string }> = {
    'explorer.programs': {
        labelKey: 'export.channelsAndPrograms',
        descriptionKey: 'export.channelsAndProgramsDescription',
    },
    'explorer.brands': {
        labelKey: 'export.sponsors',
        descriptionKey: 'export.sponsorsDescription',
    },
    [SPONSORSHIPS_REPORT_TYPE]: {
        labelKey: 'export.sponsorships',
        descriptionKey: 'export.sponsorshipsDescription',
    },
    [LIVESTREAMS_CHANNELS_REPORT_TYPE]: {
        labelKey: 'export.livestreamChannels',
        descriptionKey: 'export.livestreamChannelsDescription',
    },
    [LIVESTREAMS_PROGRAMS_REPORT_TYPE]: {
        labelKey: 'export.livestreamPrograms',
        descriptionKey: 'export.livestreamProgramsDescription',
    },
    [LIVESTREAM_CHANNEL_REPORT_TYPE]: {
        labelKey: 'export.channelStreams',
        descriptionKey: 'export.channelStreamsDescription',
    },
    [LIVESTREAM_PROGRAM_REPORT_TYPE]: {
        labelKey: 'export.programStreams',
        descriptionKey: 'export.programStreamsDescription',
    },
    [AUDIENCE_PROGRAMS_REPORT_TYPE]: {
        labelKey: 'export.audienceChannels',
        descriptionKey: 'export.audienceChannelsDescription',
    },
    [AUDIENCE_STREAMS_REPORT_TYPE]: {
        labelKey: 'export.audienceStreams',
        descriptionKey: 'export.audienceStreamsDescription',
    },
    [COMPETITION_REPORT_TYPE]: {
        labelKey: 'export.competition',
        descriptionKey: 'export.competitionDescription',
    },
    [SOCIAL_BRANDS_REPORT_TYPE]: {
        labelKey: 'export.clipSponsors',
        descriptionKey: 'export.clipSponsorsDescription',
    },
    [SOCIAL_CHANNELS_REPORT_TYPE]: {
        labelKey: 'export.clipChannels',
        descriptionKey: 'export.clipChannelsDescription',
    },
    [SOCIAL_CHANNEL_REPORT_TYPE]: {
        labelKey: 'export.clipChannel',
        descriptionKey: 'export.clipChannelDescription',
    },
};

/** Report cards of the export dialog. Chat messages store older labels, so cards are relabeled from here. */
export function exportReportCard(reportType: string): { label: string; description: string } {
    const keys = EXPORT_REPORT_CARD_KEYS[reportType];
    if (!keys) return { label: reportType, description: '' };
    return {
        label: i18n.t(keys.labelKey, { ns: 'explorer' }),
        description: i18n.t(keys.descriptionKey, { ns: 'explorer' }),
    };
}

/** @deprecated Use exportReportCard so the active language applies. */
export const EXPORT_REPORT_CARDS: Record<string, { label: string; description: string }> = {
    'explorer.programs': {
        label: 'On Demand',
        description: 'Una fila por programa con métricas On Demand del período.',
    },
    'explorer.brands': { label: 'Auspiciadores', description: 'Una fila por marca.' },
    [SPONSORSHIPS_REPORT_TYPE]: {
        label: SPONSORSHIPS_REPORT_LABEL,
        description: SPONSORSHIPS_REPORT_DESCRIPTION,
    },
    [LIVESTREAMS_CHANNELS_REPORT_TYPE]: {
        label: 'Canales',
        description: 'Una fila por canal con métricas de livestreams del período.',
    },
    [LIVESTREAMS_PROGRAMS_REPORT_TYPE]: {
        label: 'Programas',
        description: 'Una fila por programa con métricas de livestreams del período.',
    },
    [LIVESTREAM_CHANNEL_REPORT_TYPE]: {
        label: 'Livestreams del canal',
        description: 'Una fila por livestream del canal. Un programa elegido incluye métricas diarias y espectadores en vivo.',
    },
    [LIVESTREAM_PROGRAM_REPORT_TYPE]: {
        label: 'Livestreams del programa',
        description: 'Una fila por livestream del programa, más métricas diarias y espectadores en vivo.',
    },
    [AUDIENCE_PROGRAMS_REPORT_TYPE]: {
        label: 'Canales y programas',
        description: 'Mapa de audiencia y ranking de canales y programas en las franjas elegidas.',
    },
    [AUDIENCE_STREAMS_REPORT_TYPE]: {
        label: 'Canales y streams',
        description: 'Mapa de audiencia y ranking de canales y streams en las franjas elegidas.',
    },
    [COMPETITION_REPORT_TYPE]: {
        label: 'Competencia',
        description: 'Una fila por livestream de la franja, con promedio, espectadores ahora, peak y duración.',
    },
    [SOCIAL_BRANDS_REPORT_TYPE]: {
        label: 'Auspiciadores clips',
        description: 'Una fila por marca con métricas de clips del período.',
    },
    [SOCIAL_CHANNELS_REPORT_TYPE]: {
        label: 'Clips',
        description: 'Una fila por canal con métricas de clips del período.',
    },
    [SOCIAL_CHANNEL_REPORT_TYPE]: {
        label: 'Clips del canal',
        description: 'Todo el contenido del canal: clips, posts y el desglose por plataforma.',
    },
};

/** The brand × program report needs both Auspiciadores and Programas. */
export function canExportSponsorships(has: HasExplorerTabAccess): boolean {
    return has(EXPLORER_BRANDS_TAB) && (has(EXPLORER_PROGRAMS_TAB) || has(EXPLORER_LEGACY_COMPARE_TAB));
}

/** Export reports the user's Explorer sections allow, in dialog order. */
export function accessibleExportReports(has: HasExplorerTabAccess): string[] {
    return [
        ...(has(EXPLORER_PROGRAMS_TAB) || has(EXPLORER_LEGACY_COMPARE_TAB) ? ['explorer.programs'] : []),
        ...(has(EXPLORER_BRANDS_TAB) ? ['explorer.brands'] : []),
        ...(canExportSponsorships(has) ? [SPONSORSHIPS_REPORT_TYPE] : []),
    ];
}

/**
 * Reports a detail page can open preselected on its entity, own report first. Auspiciadores
 * only filters by brand and On Demand only by channel or program.
 */
export function entityExportReports(has: HasExplorerTabAccess, entity: 'channel' | 'program' | 'brand'): string[] {
    const accessible = accessibleExportReports(has);
    return entity === 'brand'
        ? accessible.filter((r) => r !== 'explorer.programs')
        : accessible.filter((r) => r !== 'explorer.brands');
}

/** Export dialog report cards for the given report types. */
export function exportAlternatives(reportTypes: string[]) {
    return reportTypes.map((reportType) => ({ reportType, ...exportReportCard(reportType) }));
}

export function getAccessibleExplorerTabs(hasAccess: HasExplorerTabAccess): ExplorerTabDef[] {
    return EXPLORER_TABS.filter((tab) => tab.canAccess(hasAccess));
}

export type ExplorerMediaFormat = 'ondemand' | 'livestreams' | 'clips';

export function isExplorerClipsBrandPath(pathname: string): boolean {
    return pathname.startsWith(`${ROUTES_NAMES.EXPLORER}/clips/brands`);
}

/** Clips IA Canales hub at `/explorador/canales`. */
export function isExplorerCanalesHubPath(pathname: string): boolean {
    return (
        pathname === ROUTES_NAMES.EXPLORER_CANALES ||
        pathname.startsWith(`${ROUTES_NAMES.EXPLORER_CANALES}/`)
    );
}

/** Company can open the Canales hub when any format child is available. */
export function canAccessExplorerCanalesHub(hasAccess: HasExplorerTabAccess): boolean {
    return (
        hasAccess(EXPLORER_PROGRAMS_TAB) ||
        hasAccess(EXPLORER_LEGACY_COMPARE_TAB) ||
        hasAccess(EXPLORER_LIVESTREAMS_TAB) ||
        hasAccess(EXPLORER_SOCIAL_TAB)
    );
}

/** Format section cards for the Canales hub bundle export, permission-gated. */
export function canalesHubSectionChoices(hasAccess: HasExplorerTabAccess): Array<{
    key: string;
    label: string;
    description: string;
}> {
    const sections: Array<{ key: string; labelKey: string; descriptionKey: string }> = [
        ...(hasAccess(EXPLORER_PROGRAMS_TAB) || hasAccess(EXPLORER_LEGACY_COMPARE_TAB)
            ? [
                  {
                      key: CANALES_SECTION_ONDEMAND,
                      labelKey: 'export.canalesSectionOnDemand',
                      descriptionKey: 'export.canalesSectionOnDemandDescription',
                  },
              ]
            : []),
        ...(hasAccess(EXPLORER_LIVESTREAMS_TAB)
            ? [
                  {
                      key: CANALES_SECTION_LIVESTREAMS,
                      labelKey: 'export.canalesSectionLivestreams',
                      descriptionKey: 'export.canalesSectionLivestreamsDescription',
                  },
              ]
            : []),
        ...(hasAccess(EXPLORER_SOCIAL_TAB)
            ? [
                  {
                      key: CANALES_SECTION_CLIPS,
                      labelKey: 'export.canalesSectionClips',
                      descriptionKey: 'export.canalesSectionClipsDescription',
                  },
              ]
            : []),
    ];
    return sections.map((section) => ({
        key: section.key,
        label: i18n.t(section.labelKey, { ns: 'explorer' }),
        description: i18n.t(section.descriptionKey, { ns: 'explorer' }),
    }));
}

export type ExplorerSidebarFormat = ExplorerMediaFormat;

export type ExplorerSidebarChildDef = {
    format: ExplorerSidebarFormat;
    labelKey: 'format.ondemand' | 'format.livestreams' | 'format.clips';
    path: string;
    /** Set on Auspiciadores children that share `/auspiciadores`. */
    sponsorMediaFormat?: 'ondemand' | 'clips';
};

export type ExplorerSidebarGroupDef = {
    index: number;
    labelKey: 'tabs.channels' | 'tabs.sponsors';
    children: ExplorerSidebarChildDef[];
};

function explorerSidebarAccessMap(hasAccess: HasExplorerTabAccess) {
    const tabs = getAccessibleExplorerTabs(hasAccess).filter(
        (tab) => tab.index !== EXPLORER_GENERAL_TAB,
    );
    return new Map(tabs.map((tab) => [tab.index, tab]));
}

/** Sidebar items. Clips IA collapses Livestreams and Clips into the Canales / Auspiciadores pillars. */
export function getExplorerSidebarTabs(
    hasAccess: HasExplorerTabAccess,
    clipsIa: boolean,
): ExplorerTabDef[] {
    const tabs = getAccessibleExplorerTabs(hasAccess).filter(
        (tab) => tab.index !== EXPLORER_GENERAL_TAB,
    );
    if (!clipsIa) return tabs;

    const byIndex = explorerSidebarAccessMap(hasAccess);
    const programs = byIndex.get(EXPLORER_PROGRAMS_TAB);
    const livestreams = byIndex.get(EXPLORER_LIVESTREAMS_TAB);
    const clips = byIndex.get(EXPLORER_SOCIAL_TAB);
    const brands = byIndex.get(EXPLORER_BRANDS_TAB);
    const items: ExplorerTabDef[] = [];

    if (programs || livestreams || clips) {
        items.push({
            index: EXPLORER_PROGRAMS_TAB,
            labelKey: 'tabs.channels',
            label: 'Canales',
            path: ROUTES_NAMES.EXPLORER_CANALES,
            icon: VideoLibraryIcon,
            canAccess: () => true,
        });
    }
    if (brands || clips) {
        items.push({
            index: EXPLORER_BRANDS_TAB,
            labelKey: 'tabs.sponsors',
            label: 'Auspiciadores',
            path: (brands ?? EXPLORER_TABS.find((tab) => tab.index === EXPLORER_BRANDS_TAB))!.path,
            icon: CampaignIcon,
            canAccess: () => true,
        });
    }
    return items;
}

/** Nested Explorador groups for Clips IA. Empty when the company does not use that IA. */
export function getExplorerSidebarGroups(
    hasAccess: HasExplorerTabAccess,
    clipsIa: boolean,
): ExplorerSidebarGroupDef[] {
    if (!clipsIa) return [];
    const byIndex = explorerSidebarAccessMap(hasAccess);
    const programs = byIndex.get(EXPLORER_PROGRAMS_TAB);
    const livestreams = byIndex.get(EXPLORER_LIVESTREAMS_TAB);
    const clips = byIndex.get(EXPLORER_SOCIAL_TAB);
    const brands = byIndex.get(EXPLORER_BRANDS_TAB);
    const groups: ExplorerSidebarGroupDef[] = [];

    const channelChildren: ExplorerSidebarChildDef[] = [];
    if (programs) {
        channelChildren.push({
            format: 'ondemand',
            labelKey: 'format.ondemand',
            path: ROUTES_NAMES.EXPLORER_PROGRAMS_LIST,
        });
    }
    if (livestreams) {
        channelChildren.push({
            format: 'livestreams',
            labelKey: 'format.livestreams',
            path: ROUTES_NAMES.EXPLORER_LIVESTREAMS_LIST,
        });
    }
    if (clips) {
        channelChildren.push({
            format: 'clips',
            labelKey: 'format.clips',
            path: ROUTES_NAMES.EXPLORER_SOCIAL_LIST,
        });
    }
    if (channelChildren.length > 0) {
        groups.push({
            index: EXPLORER_PROGRAMS_TAB,
            labelKey: 'tabs.channels',
            children: channelChildren,
        });
    }

    const sponsorChildren: ExplorerSidebarChildDef[] = [];
    if (brands) {
        sponsorChildren.push({
            format: 'ondemand',
            labelKey: 'format.ondemand',
            path: ROUTES_NAMES.EXPLORER_BRANDS_LIST,
            sponsorMediaFormat: 'ondemand',
        });
    }
    if (clips) {
        sponsorChildren.push({
            format: 'clips',
            labelKey: 'format.clips',
            path: ROUTES_NAMES.EXPLORER_BRANDS_LIST,
            sponsorMediaFormat: 'clips',
        });
    }
    if (sponsorChildren.length > 0) {
        groups.push({
            index: EXPLORER_BRANDS_TAB,
            labelKey: 'tabs.sponsors',
            children: sponsorChildren,
        });
    }
    return groups;
}

export function isExplorerSidebarChildActive(
    child: ExplorerSidebarChildDef,
    pathname: string,
    sponsorMediaFormat: 'ondemand' | 'clips',
): boolean {
    if (child.sponsorMediaFormat) {
        if (isExplorerClipsBrandPath(pathname)) return child.sponsorMediaFormat === 'clips';
        if (getActiveExplorerTab(pathname) !== EXPLORER_BRANDS_TAB) return false;
        return sponsorMediaFormat === child.sponsorMediaFormat;
    }
    // Hub highlights the Canales group header, not On demand / Livestreams / Clips.
    if (isExplorerCanalesHubPath(pathname)) return false;
    const tab = getActiveExplorerTab(pathname);
    if (child.format === 'ondemand') return tab === EXPLORER_PROGRAMS_TAB;
    if (child.format === 'livestreams') return tab === EXPLORER_LIVESTREAMS_TAB;
    return tab === EXPLORER_SOCIAL_TAB && !isExplorerClipsBrandPath(pathname);
}

export function getExplorerMediaFormat(pathname: string): ExplorerMediaFormat {
    const tab = getActiveExplorerTab(pathname);
    if (tab === EXPLORER_LIVESTREAMS_TAB) return 'livestreams';
    if (tab === EXPLORER_SOCIAL_TAB) return 'clips';
    return 'ondemand';
}

export type ExplorerListTitleKey =
    | 'tabs.panel'
    | 'tabs.channels'
    | 'format.ondemand'
    | 'format.livestreams'
    | 'format.clips';

/** Page h1: format name on Canales / Auspiciadores lists; Mi panel stays the panel title. */
export function getExplorerListTitleKey(
    pathname: string,
    sponsorsFormat: 'ondemand' | 'clips' = 'ondemand',
): ExplorerListTitleKey {
    const tab = getActiveExplorerTab(pathname);
    if (tab === EXPLORER_GENERAL_TAB) return 'tabs.panel';
    if (isExplorerCanalesHubPath(pathname)) return 'tabs.channels';
    if (
        isExplorerClipsBrandPath(pathname) ||
        (tab === EXPLORER_BRANDS_TAB && sponsorsFormat === 'clips')
    ) {
        return 'format.clips';
    }
    const format = getExplorerMediaFormat(pathname);
    if (format === 'livestreams') return 'format.livestreams';
    if (format === 'clips') return 'format.clips';
    return 'format.ondemand';
}

/** Sidebar highlight: under Clips IA, livestreams and clip-channel pages belong to Canales. */
export function getActiveExplorerPillar(pathname: string, clipsIa: boolean): number {
    if (!clipsIa) return getActiveExplorerTab(pathname);
    if (isExplorerCanalesHubPath(pathname)) return EXPLORER_PROGRAMS_TAB;
    if (isExplorerClipsBrandPath(pathname)) return EXPLORER_BRANDS_TAB;
    const tab = getActiveExplorerTab(pathname);
    if (tab === EXPLORER_LIVESTREAMS_TAB || tab === EXPLORER_SOCIAL_TAB) {
        return EXPLORER_PROGRAMS_TAB;
    }
    return tab;
}

/** Clips IA can open Auspiciadores for clip brands even without explorer-sponsors. */
export function canOpenExplorerSection(
    tabIndex: number,
    hasAccess: HasExplorerTabAccess,
    clipsIa: boolean,
): boolean {
    if (getAccessibleExplorerTabs(hasAccess).some((tab) => tab.index === tabIndex)) return true;
    return clipsIa && tabIndex === EXPLORER_BRANDS_TAB && hasAccess(EXPLORER_SOCIAL_TAB);
}

export const EXPLORER_PANEL_TAB = EXPLORER_TABS.find((tab) => tab.index === EXPLORER_GENERAL_TAB)!;

/** Mi panel opens for anyone with at least one Explorer section. */
export function canAccessExplorerPanel(hasAccess: HasExplorerTabAccess): boolean {
    return EXPLORER_PANEL_TAB.canAccess(hasAccess);
}

/** Which Explorer section a path belongs to: a section URL, or the section owning a detail page. */
export function getActiveExplorerTab(pathname: string): number {
    const section = EXPLORER_TABS.find((tab) => pathname === tab.path);
    if (section) return section.index;
    const base = `${ROUTES_NAMES.EXPLORER}/`;
    const sub = pathname.startsWith(base) ? pathname.slice(base.length).split('/')[0] : '';
    if (sub === 'canales') return EXPLORER_PROGRAMS_TAB;
    if (sub === 'brands') return EXPLORER_BRANDS_TAB;
    if (sub === 'programs' || sub === 'podcasts') return EXPLORER_PROGRAMS_TAB;
    if (sub === 'clips' || sub === 'social') return EXPLORER_SOCIAL_TAB;
    if (sub === 'livestreams') return EXPLORER_LIVESTREAMS_TAB;
    return EXPLORER_GENERAL_TAB;
}
